/**
 * Task structure (spec §9.3) over a plain snapshot of the core doc — mirror of `structure.py`.
 * `blocked`, `active`, cycle detection and write validation live here; ops.ts calls `validateEdge`
 * before committing.
 */
export type EdgeKind = "depends" | "relates";

export interface Edge {
  kind: EdgeKind;
  from: string;
  to: string;
}

export interface SnapshotValue {
  tasks?: Record<string, unknown>;
  edges?: Record<string, Edge>;
  sequences?: Record<string, { id?: string; title?: string; items?: string[]; routine?: boolean }>;
  groups?: Record<
    string,
    { id?: string; title?: string; color?: string | null; members?: Record<string, boolean> }
  >;
  tree?: TreeNode[];
}

interface TreeNode {
  id: string;
  meta?: { taskId?: string };
  children?: TreeNode[];
  fractional_index?: string;
}

type TaskRec = Record<string, unknown>;

export class Snapshot {
  tasks: Record<string, TaskRec>;
  edges: Record<string, Edge>;
  sequences: NonNullable<SnapshotValue["sequences"]>;
  groups: NonNullable<SnapshotValue["groups"]>;
  tree: TreeNode[];
  private parent = new Map<string, string | null>();
  private children = new Map<string, string[]>();

  constructor(v: SnapshotValue) {
    this.tasks = {};
    for (const [k, t] of Object.entries(v.tasks ?? {})) {
      if (t && typeof t === "object" && !Array.isArray(t)) this.tasks[k] = t as TaskRec;
    }
    this.edges = v.edges ?? {};
    this.sequences = v.sequences ?? {};
    this.groups = v.groups ?? {};
    this.tree = v.tree ?? [];
    const walk = (items: TreeNode[], parent: string | null) => {
      for (const n of items) {
        const tid = n.meta?.taskId;
        if (typeof tid === "string") {
          this.parent.set(tid, parent);
          const key = parent ?? "";
          if (!this.children.has(key)) this.children.set(key, []);
          this.children.get(key)!.push(tid);
        }
        walk(n.children ?? [], typeof tid === "string" ? tid : parent);
      }
    };
    walk(this.tree, null);
  }

  parentOf(id: string): string | null {
    return this.parent.get(id) ?? null;
  }
  childrenOf(id: string | null): string[] {
    return [...(this.children.get(id ?? "") ?? [])];
  }
  isDone(id: string): boolean {
    return Boolean(this.tasks[id]?.doneAt);
  }
  isOpen(id: string): boolean {
    const t = this.tasks[id];
    return Boolean(t) && !t.doneAt && !t.archivedAt;
  }
}

export function dependsEdges(s: Snapshot): [string, string, string][] {
  const out: [string, string, string][] = [];
  for (const [eid, e] of Object.entries(s.edges)) {
    if (e.kind === "depends" && e.from in s.tasks && e.to in s.tasks) out.push([eid, e.from, e.to]);
  }
  return out;
}

export function relatesOf(s: Snapshot, id: string): string[] {
  const out: string[] = [];
  for (const e of Object.values(s.edges)) {
    if (e.kind !== "relates") continue;
    if (e.from === id && e.to in s.tasks) out.push(e.to);
    else if (e.to === id && e.from in s.tasks) out.push(e.from);
  }
  return out;
}

function adjacency(s: Snapshot): Map<string, string[]> {
  const adj = new Map<string, string[]>();
  for (const [, a, b] of dependsEdges(s)) {
    if (!adj.has(a)) adj.set(a, []);
    adj.get(a)!.push(b);
  }
  return adj;
}

/** Back-edges (from→to) on cycles, found by DFS; `blocked` ignores them. */
export function findCycles(s: Snapshot): Set<string> {
  const adj = adjacency(s);
  const colour = new Map<string, 1 | 2>();
  const back = new Set<string>();
  const visit = (u: string) => {
    colour.set(u, 1);
    for (const v of adj.get(u) ?? []) {
      const c = colour.get(v);
      if (c === 1) back.add(`${u}\u0000${v}`);
      else if (c === undefined) visit(v);
    }
    colour.set(u, 2);
  };
  for (const node of [...adj.keys()].sort()) if (!colour.has(node)) visit(node);
  return back;
}

export function blockers(s: Snapshot, id: string): string[] {
  const back = findCycles(s);
  const out: string[] = [];
  for (const [, a, b] of dependsEdges(s)) {
    if (a === id && !back.has(`${a}\u0000${b}`) && s.isOpen(b)) out.push(b);
  }
  return out;
}

export function blocked(s: Snapshot, id: string): boolean {
  return blockers(s, id).length > 0;
}

export function wouldCreateCycle(s: Snapshot, from: string, to: string): boolean {
  if (from === to) return true;
  const adj = adjacency(s);
  const seen = new Set<string>();
  const stack = [to];
  while (stack.length) {
    const u = stack.pop()!;
    if (u === from) return true;
    if (seen.has(u)) continue;
    seen.add(u);
    stack.push(...(adj.get(u) ?? []));
  }
  return false;
}

export function isAncestor(s: Snapshot, ancestor: string, id: string): boolean {
  let p = s.parentOf(id);
  while (p !== null) {
    if (p === ancestor) return true;
    p = s.parentOf(p);
  }
  return false;
}

export interface Validation {
  ok: boolean;
  error?: string;
  warning?: string;
}

export function validateEdge(s: Snapshot, kind: EdgeKind, from: string, to: string): Validation {
  if (kind !== "depends" && kind !== "relates") return { ok: false, error: "structure.badEdgeKind" };
  if (from === to) return { ok: false, error: "structure.selfEdge" };
  if (!(from in s.tasks) || !(to in s.tasks)) return { ok: false, error: "structure.noSuchTask" };
  if (kind === "relates") return { ok: true };
  if (wouldCreateCycle(s, from, to)) return { ok: false, error: "structure.cycle" };
  if (isAncestor(s, from, to) || isAncestor(s, to, from))
    return { ok: true, warning: "structure.ancestorDependency" };
  return { ok: true };
}

export function sequenceItems(s: Snapshot, seqId: string): string[] {
  return (s.sequences[seqId]?.items ?? []).filter((i) => i in s.tasks);
}

export function sequenceHead(s: Snapshot, seqId: string): string | null {
  for (const tid of sequenceItems(s, seqId)) {
    if (!s.isDone(tid) && !s.tasks[tid].archivedAt) return tid;
  }
  return null;
}

export function sequencesOf(s: Snapshot, id: string): string[] {
  return Object.keys(s.sequences).filter((sid) => sequenceItems(s, sid).includes(id));
}

/**
 * The first open subtask of a parent with an *Ordered checklist* (UI cleanup U3: the ordered checklist is the
 * sequence of the subtasks — the others are queued, as in a sequence; before, it only highlighted the next one).
 */
export function orderedHead(s: Snapshot, parentId: string): string | null {
  for (const c of s.childrenOf(parentId)) if (s.isOpen(c)) return c;
  return null;
}

/**
 * Only the head of every sequence it belongs to, and of its parent's ordered checklist, is active (spec §9.3). Rotating
 * subtasks are parts of their task, never active on their own (ADR 0035).
 */
export function active(s: Snapshot, id: string): boolean {
  const parent = s.parentOf(id);
  const pp = parent ? (s.tasks[parent]?.props as Record<string, unknown> | undefined) : undefined;
  if (pp?.["rec.rotateSubtasks"] === true) return false;
  if (parent && s.tasks[parent]?.orderedChildren && orderedHead(s, parent) !== id) return false;
  return sequencesOf(s, id).every((sid) => sequenceHead(s, sid) === id);
}

export function sequenceCompleted(s: Snapshot, seqId: string): boolean {
  const items = sequenceItems(s, seqId);
  return items.length > 0 && items.every((t) => s.isDone(t));
}

export function groupMembers(s: Snapshot, groupId: string): string[] {
  return Object.entries(s.groups[groupId]?.members ?? {})
    .filter(([tid, on]) => on && tid in s.tasks)
    .map(([tid]) => tid);
}

export function groupsOf(s: Snapshot, id: string): string[] {
  return Object.keys(s.groups).filter((gid) => Boolean(s.groups[gid].members?.[id]));
}

export function groupCompleted(s: Snapshot, groupId: string): boolean {
  const members = groupMembers(s, groupId).filter((t) => !s.tasks[t].archivedAt);
  return members.length > 0 && members.every((t) => s.isDone(t));
}

export function depth(s: Snapshot, id: string): number {
  let d = 0;
  let p = s.parentOf(id);
  while (p !== null) {
    d++;
    p = s.parentOf(p);
  }
  return d;
}

export function wouldCreateTreeCycle(s: Snapshot, id: string, newParent: string | null): boolean {
  if (newParent === null) return false;
  if (newParent === id) return true;
  return isAncestor(s, id, newParent);
}
